import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// 매출단가/매입단가에 천단위 구분기호를 붙인다. 값이 숫자가 아니면(빈칸 등) 원본을 그대로 보여준다.
const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('ko-KR') : value;
};

// 확인/선택 셀(Select)에 표시할 문구 - 닫힌 상태에서는 CSS로 말줄임(ellipsis) 처리되므로
// Tooltip에 전체 문구를 그대로 보여준다.
const suggestionPlaceholder = (row) =>
  `선택 필요 — 혹시: ${(row.suggestions || []).map((s) => s.productNm).join(', ')}`;

const suggestionSelectedLabel = (row, decision) => {
  if (decision === 'NEW') return '신규로 등록';
  if (decision) {
    const picked = (row.suggestions || []).find(
      (s) => s.productUuid === decision,
    );
    if (picked) return `기존 제품으로 사용: ${picked.productNm}`;
  }
  return suggestionPlaceholder(row);
};

/**
 * 원가산출 엑셀 업로드 결과 미리보기 - 행별 오류(errorMsg)를 보여주고, "정상 N행 추가"를 누르면
 * 오류 행은 제외하고 정상 행만 상위로 올려보낸다 (2026-10-27 security-patches).
 *
 * 백엔드(ExpenseService#uploadFileList, 672d3b4/1c24af4 커밋)가 셀이 비었거나 형식이 안 맞는
 * 행, 또는 보정 후에도 매입처/제품명 등 필수 항목이 빈 행에 errorMsg를 붙여 돌려준다. 업로드
 * 자체는 전체가 막히지 않으니, 화면에서도 오류 행을 보여주고 "제외하고 추가"로 처리한다(막지
 * 않고 건너뛴다 - 코디네이터 결정: exclude + 안내 문구).
 *
 * 2026-10-27 cost-excel-roundtrip: 매입처가 "거래처 미등록"으로 걸린 행은 매입처 칸이
 * 비어 보이던 문제를 고친다 - 백엔드(sellPartnerInputNm, ExpenseDto)가 엑셀에 실제로 입력된
 * 매입처 이름을 errorMsg와 별도 필드로 돌려주므로, 그 이름을 회색 기울임 글자 + "미등록" 칩으로
 * 보여준다(매칭 성공 행의 sellPartnerNm 표시와 구분).
 *
 * 2026-10-27 cost-excel-roundtrip: 백엔드가 제품 매칭 상태(matchStatus)도 같이 돌려준다 -
 * MATCHED(제품코드/제품명으로 정확히 찾음) / SUGGEST(비슷한 후보가 있음 - 사용자가 직접
 * "기존 제품으로 사용" 또는 "신규로 등록" 중 골라야 한다, 기본값 없음) / NEW(후보조차 없음).
 * 서버는 절대 후보를 자동으로 확정하지 않는다 - 선택은 여기, 사람이 한다(owner decision 3).
 *
 * [버그수정, Fable 사전검토 3-2/6] NEW 행의 기본값을 "신규 제품으로 등록"에서 "기타로
 * 추가"로 바꿨다 - 원가산출 현재 내역을 다운로드해서(제품코드 칸이 기타 라인은 빈칸, ksm_backend
 * 5fa3edb) 그대로 다시 올리면, 자유 텍스트 기타 라인(인건비성 등)은 제품마스터 어디에도
 * 이름이 안 맞아 전부 matchStatus=NEW가 된다. 예전엔 NEW를 자동으로 "신규 제품 등록"으로
 * 처리해서 왕복마다 제품마스터에 "포장비" 같은 라인이 제품으로 등록됐다. 이제 NEW도 SUGGEST
 * 처럼 선택 칸을 보여주되 기본값은 "기타로 추가"(등록 안 함)이고, "신규로 등록"을 명시적으로
 * 고른 행만 등록한다 - 진짜 신규 제품을 올릴 때는 한 번 더 클릭이 필요해졌지만, 왕복 때마다
 * 조용히 제품이 쌓이는 쪽보다 안전하다.
 */
export const summarizeUploadResult = (list, decisions = {}) => {
  const rows = Array.isArray(list) ? list : [];
  const errorRows = rows.filter((row) => Boolean(row?.errorMsg));

  const isUndecidedSuggestion = (row) =>
    !row?.errorMsg &&
    row?.matchStatus === 'SUGGEST' &&
    !decisions[row.expUuid];

  // NEW는 SUGGEST와 달리 선택을 안 해도 막히지 않는다(기본값 = "기타로 추가") - 추가 대상에서
  // 빼지 않는다. isUndecidedSuggestion과 별도로 둔 이유: NEW는 확인 버튼을 막을 필요가 없다.

  const pendingRows = rows.filter(isUndecidedSuggestion);

  const validRows = rows
    .filter((row) => !row?.errorMsg && !isUndecidedSuggestion(row))
    .map((row) => resolveRowDecision(row, decisions));

  const parts = [];
  if (errorRows.length > 0) {
    parts.push(`오류 ${errorRows.length}행 — 고친 뒤 다시 올려주세요.`);
  }
  if (pendingRows.length > 0) {
    parts.push(`제안 ${pendingRows.length}행 — 추가 전에 선택이 필요합니다.`);
  }

  return { validRows, errorRows, pendingRows, message: parts.join(' ') };
};

// SUGGEST 행의 사용자 선택을 실제 반영한 결과 행을 만든다. 서버가 자동으로 고르지 않고,
// 여기서 사람이 고른 값만 그대로 적용한다.
const resolveRowDecision = (row, decisions) => {
  const decision = decisions[row.expUuid];

  if (row.matchStatus === 'NEW') {
    // 후보가 없음 - 기본값은 "기타로 추가"(등록 안 함, masterTyp/itemCd는 서버가 돌려준 값
    // 그대로 - 기타 라인은 'E'/'기타'). "신규로 등록"을 명시적으로 고른 행만 제품마스터에
    // 새로 등록한다(상위 컴포넌트가 처리).
    if (decision === 'REGISTER') {
      return { ...row, __createProduct: true };
    }
    return row;
  }

  if (row.matchStatus === 'SUGGEST') {
    if (decision === 'NEW') {
      return { ...row, __createProduct: true };
    }
    if (decision) {
      // 후보 중 하나를 "기존 제품으로 사용"으로 선택함 - productUuid를 그 제품으로 확정한다.
      const picked = (row.suggestions || []).find(
        (s) => s.productUuid === decision,
      );
      return {
        ...row,
        productUuid: decision,
        productCd: picked?.productCd || row.productCd,
        matchStatus: 'MATCHED',
      };
    }
  }

  return row;
};

const StatusChip = ({ row }) => {
  if (row.matchStatus === 'NEW') {
    return <Chip size="small" color="info" label="신규 제품" />;
  }
  if (row.matchStatus === 'SUGGEST') {
    return <Chip size="small" color="warning" label="제안" />;
  }
  return null;
};

const UploadResultReview = ({ rows, onConfirm, onCancel }) => {
  // expUuid -> 'NEW' | <선택한 productUuid> - SUGGEST 행에서 사용자가 고른 값.
  const [decisions, setDecisions] = useState({});

  const { validRows, errorRows, pendingRows, message } = summarizeUploadResult(
    rows,
    decisions,
  );

  const handleDecide = (expUuid, value) => {
    setDecisions((prev) => ({ ...prev, [expUuid]: value }));
  };

  return (
    <Box>
      {(errorRows.length > 0 || pendingRows.length > 0) && (
        <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
          {message}
        </Typography>
      )}
      <TableContainer>
        <Table size="small" sx={{ minWidth: 970, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap', width: 90 }}>
                상태
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                제품명
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', width: 60 }}>
                수량
              </TableCell>
              <TableCell
                align="right"
                sx={{ whiteSpace: 'nowrap', width: 100 }}
              >
                매출단가
              </TableCell>
              <TableCell
                align="right"
                sx={{ whiteSpace: 'nowrap', width: 100 }}
              >
                매입단가
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>
                매입처
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 280 }}>
                확인/선택
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((row, index) => {
              const isError = Boolean(row.errorMsg);
              const isPartnerMissing =
                isError && row.errorMsg.includes('거래처 미등록');
              const decision = decisions[row.expUuid];

              return (
                <TableRow
                  key={row.expUuid || index}
                  sx={isError ? { backgroundColor: '#fdecea' } : undefined}
                >
                  <TableCell sx={{ width: 90 }}>
                    <StatusChip row={row} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Tooltip title={row.productNm || ''}>
                      <Box
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}
                      >
                        {row.productNm}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 60 }}>
                    {row.expCnt}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      whiteSpace: 'nowrap',
                      width: 100,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatAmount(row.unitVal)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      whiteSpace: 'nowrap',
                      width: 100,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatAmount(row.priceVal)}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {isPartnerMissing && row.sellPartnerInputNm ? (
                      <Tooltip title={`엑셀에 입력된 매입처: ${row.sellPartnerInputNm}`}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: 220,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.disabled',
                              fontStyle: 'italic',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.sellPartnerInputNm}
                          </Typography>
                          <Chip size="small" color="default" variant="outlined" label="미등록" />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title={row.sellPartnerNm || ''}>
                        <Box
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 220,
                          }}
                        >
                          {row.sellPartnerNm}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {isError && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ display: 'block', wordBreak: 'break-word' }}
                        >
                          {row.errorMsg}
                        </Typography>
                        {isPartnerMissing && (
                          <Box>
                            <Link
                              href="/partnermng/add"
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                            >
                              거래처관리에서 등록하기
                            </Link>
                          </Box>
                        )}
                      </Box>
                    )}
                    {!isError && row.matchStatus === 'SUGGEST' && (
                      <Tooltip title={suggestionSelectedLabel(row, decision)}>
                        <Select
                          size="small"
                          displayEmpty
                          value={decision || ''}
                          onChange={(e) =>
                            handleDecide(row.expUuid, e.target.value)
                          }
                          sx={{
                            width: '100%',
                            maxWidth: 280,
                            '& .MuiSelect-select': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {suggestionPlaceholder(row)}
                          </MenuItem>
                          {(row.suggestions || []).map((s) => (
                            <MenuItem
                              key={s.productUuid}
                              value={s.productUuid}
                            >
                              기존 제품으로 사용: {s.productNm}
                            </MenuItem>
                          ))}
                          <MenuItem value="NEW">신규로 등록</MenuItem>
                        </Select>
                      </Tooltip>
                    )}
                    {!isError && row.matchStatus === 'NEW' && (
                      <Select
                        size="small"
                        value={decision === 'REGISTER' ? 'REGISTER' : ''}
                        onChange={(e) =>
                          handleDecide(row.expUuid, e.target.value || undefined)
                        }
                        sx={{
                          width: '100%',
                          maxWidth: 280,
                          '& .MuiSelect-select': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                          },
                        }}
                      >
                        <MenuItem value="">기타로 추가</MenuItem>
                        <MenuItem value="REGISTER">신규 제품으로 등록</MenuItem>
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogActions>
        <Button onClick={onCancel}>취소</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => onConfirm(validRows)}
          disabled={validRows.length === 0}
        >
          정상 {validRows.length}행 추가
        </Button>
      </DialogActions>
    </Box>
  );
};

export default UploadResultReview;
